import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Check, Plus, Search } from 'lucide-react';
import { TAG_TINT, readableChipText, type Tag } from '@karya/core';
import { cn } from '@/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlay';

const chipStyles = new Map<string, CSSProperties>();

/** Tinted background plus a text colour that stays readable in each theme (see `.tag-chip`). */
function chipStyle(color: string): CSSProperties {
  let style = chipStyles.get(color);
  if (!style) {
    style = {
      background: `color-mix(in srgb, ${color} ${TAG_TINT * 100}%, transparent)`,
      '--chip-fg': readableChipText(color, 'light'),
      '--chip-fg-dark': readableChipText(color, 'dark'),
    } as CSSProperties;
    chipStyles.set(color, style);
  }
  return style;
}

export function TagChip({
  tag,
  className,
  onRemove,
}: {
  tag: Pick<Tag, 'name' | 'color'>;
  className?: string;
  onRemove?: () => void;
}) {
  return (
    <span
      className={cn(
        'tag-chip inline-flex h-[18px] max-w-[180px] shrink-0 items-center gap-1 rounded-xs px-1.5 text-cap whitespace-nowrap',
        className,
      )}
      style={chipStyle(tag.color)}
    >
      <span className="truncate">{tag.name}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label={`Remove tag ${tag.name}`}
          className="-mr-0.5 opacity-70 hover:opacity-100"
        >
          ×
        </button>
      )}
    </span>
  );
}

export function TagList({ tags, max = 2 }: { tags: Tag[]; max?: number }) {
  if (tags.length === 0) return null;
  const shown = tags.slice(0, max);
  return (
    <span className="flex min-w-0 shrink items-center gap-1 overflow-hidden">
      {shown.map((t) => (
        <TagChip key={t.id} tag={t} />
      ))}
      {tags.length > max && (
        <span className="inline-flex h-5 items-center rounded-xs bg-surface-2 px-1.5 text-cap text-ink-2">
          +{tags.length - max}
        </span>
      )}
    </span>
  );
}

/** Toggle tags of a space, with inline "Create tag". */
export function TagPicker({
  tags,
  selected,
  onToggle,
  onCreate,
  children,
  align = 'start',
}: {
  tags: Tag[];
  selected: string[];
  onToggle: (tagId: string, on: boolean) => void;
  onCreate: (name: string) => void;
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(
    () => tags.filter((t) => t.name.toLowerCase().includes(q.trim().toLowerCase())),
    [tags, q],
  );
  const exact = tags.some((t) => t.name.toLowerCase() === q.trim().toLowerCase());
  return (
    <Popover onOpenChange={(o) => !o && setQ('')}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-1" onClick={(e) => e.stopPropagation()}>
        <label className="flex h-8 items-center gap-2 rounded-sm bg-surface-2 px-2 text-sm text-ink-3">
          <Search className="size-3.5" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && q.trim() && !exact) {
                onCreate(q.trim());
                setQ('');
              }
            }}
            placeholder="Search or create tags"
            aria-label="Search or create tags"
            className="min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-ink-3"
          />
        </label>
        <div className="mt-1 max-h-64 overflow-y-auto scroll-thin">
          {filtered.map((t) => {
            const on = selected.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => onToggle(t.id, !on)}
                className="flex h-8 w-full items-center gap-2 rounded-sm px-2 hover:bg-surface-2"
                aria-pressed={on}
              >
                <TagChip tag={t} />
                <span className="flex-1" />
                {on && <Check className="size-4 text-primary" />}
              </button>
            );
          })}
          {q.trim() && !exact && (
            <button
              onClick={() => {
                onCreate(q.trim());
                setQ('');
              }}
              className="flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-ink-2 hover:bg-surface-2"
            >
              <Plus className="size-4" />
              Create tag “{q.trim()}”
            </button>
          )}
          {filtered.length === 0 && !q.trim() && (
            <div className="px-2 py-3 text-sm text-ink-3">
              No tags in this space yet. Type a name to create one.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
